import type { RouteRecordRaw } from 'vue-router'
import { guardManagementPage } from '@/config/router/navigationsGuards/guardManagement'

const MODULE_NAME = 'MANAGEMENT'
const MODULE_ID = undefined

const managementRoutes: Array<RouteRecordRaw> = [
  {
    path: '/management',
    meta: {
      requiresAuth: true,
      requiresManagement: true,
    },
    beforeEnter: [],
    children: [
      {
        path: 'account',
        name: 'ManagementAccounts',
        component: () => import('@/views/management/Account.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementAccounts',
        },
        beforeEnter: [guardManagementPage('ManagementAccounts')],
      },
      {
        path: 'invoice',
        name: 'ManagementInvoice',
        component: () => import('@/views/management/Invoice.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementInvoice',
        },
        beforeEnter: [guardManagementPage('ManagementInvoice')],
      },
      {
        path: 'stats',
        name: 'ManagementStats',
        component: () => import('@/views/management/Stats.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementStats',
        },
        beforeEnter: [guardManagementPage('ManagementStats')],
      },
      {
        path: 'user-activity',
        name: 'ManagementUserActivity',
        component: () => import('@/views/management/UserActivity.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementUserActivity',
          titleTranslationKey: 'modules.management.userActivity.title',
        },
        beforeEnter: [guardManagementPage('ManagementUserActivity')],
      },
      {
        path: 'signatures',
        name: 'ManagementSignatures',
        component: () => import('@/views/management/Signatures.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementSignatures',
          titleTranslationKey: 'modules.management.signatures.title',
        },
        beforeEnter: [guardManagementPage('ManagementSignatures')],
      },
      {
        path: 'pricing',
        name: 'ManagementPricing',
        component: () => import('@/views/management/Pricing.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementPricing',
        },
        beforeEnter: [guardManagementPage('ManagementPricing')],
      },
      {
        path: 'brochures',
        name: 'ManagementBrochures',
        component: () => import('@/views/management/Brochures.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementBrochures',
        },
        beforeEnter: [guardManagementPage('ManagementBrochures')],
      },
      {
        path: 'account/:clientId',
        name: 'ManagementAccount',
        component: () => import('@/views/management/ClientAccount.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementAccount',
        },
        beforeEnter: [guardManagementPage('ManagementAccount')],
      },
      {
        path: 'account/:clientId/profiles/:profileId',
        name: 'ManagementAccountProfile',
        component: () => import('@/views/superAdmin/users/profile/ProfileItem.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementAccount',
        },
        beforeEnter: [guardManagementPage('ManagementAccount')],
      },
      {
        path: 'servermanagement',
        name: 'ManagementServers',
        component: () => import('@/views/management/ServerManagement.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementServers',
        },
        beforeEnter: [guardManagementPage('ManagementServers')],
      },
      {
        path: 'e2e',
        name: 'ManagementCypressE2e',
        component: () => import('@/views/management/CypressE2e.vue'),
        meta: {
          requiresAuth: true,
          managementPage: 'ManagementCypressE2e',
          titleTranslationKey: 'modules.management.cypressE2e.title',
        },
        beforeEnter: [guardManagementPage('ManagementCypressE2e')],
      },
      {
        path: 'ai',
        children: [
          {
            path: 'history',
            name: 'ManagementAIHistory',
            component: () => import('@/views/management/ai/history.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'ManagementAIHistory',
            },
            beforeEnter: [guardManagementPage('ManagementAIHistory')],
          },
        ],
      },
      {
        path: 'academy',
        children: [
          {
            path: '',
            name: 'ManagementAcademy',
            component: () => import('@/views/management/academy/AcademyCatalog.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'ManagementAcademy',
              titleTranslationKey: 'modules.management.academy.title',
            },
            beforeEnter: [guardManagementPage('ManagementAcademy')],
          },
          {
            path: 'revisions/:revisionId',
            name: 'ManagementAcademyReview',
            component: () => import('@/views/management/academy/AcademyReview.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'ManagementAcademy',
              titleTranslationKey: 'modules.management.academy.review.title',
            },
            beforeEnter: [guardManagementPage('ManagementAcademy')],
          },
        ],
      },
      // Admin Ticket Management Routes
      {
        path: 'tickets',
        children: [
          {
            path: 'overview',
            name: 'AdminTicketOverview',
            component: () => import('@/views/management/ticket/Overview.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'AdminTicketOverview',
              titleTranslationKey: 'modules.adminTicket.overview.title',
            },
            beforeEnter: [guardManagementPage('AdminTicketOverview')],
          },
          {
            path: 'board',
            name: 'AdminTicketBoard',
            component: () => import('@/views/management/ticket/Board.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'AdminTicketBoard',
              titleTranslationKey: 'modules.adminTicket.board.title',
            },
            beforeEnter: [guardManagementPage('AdminTicketBoard')],
          },
          {
            path: 'list',
            name: 'AdminTicketList',
            component: () => import('@/views/management/ticket/List.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'AdminTicketList',
              titleTranslationKey: 'modules.adminTicket.list.title',
            },
            beforeEnter: [guardManagementPage('AdminTicketList')],
          },
          {
            path: ':ticketId',
            name: 'AdminTicketDetail',
            component: () => import('@/views/management/ticket/Detail.vue'),
            meta: {
              requiresAuth: true,
              managementPage: 'AdminTicketDetail',
              titleTranslationKey: 'modules.adminTicket.detail.title',
            },
            beforeEnter: [guardManagementPage('AdminTicketDetail')],
            props: true,
          },
        ],
      },

    ],

  },
]

export default managementRoutes.map(route => ({ ...route, meta: { ...route.meta, moduleName: MODULE_NAME, moduleId: MODULE_ID } }))
